const LOG_KEY = 'systemLog';
const MAX_AGE = 365 * 24 * 60 * 60 * 1000; // 1 year

/**
 * Reads a numeric environment variable from multiple sources
 * @param {string[]} keys - Array of environment variable keys to check
 * @param {number} defaultValue - Default value if no key is found
 * @returns {number} The resolved numeric value
 */
function readNumEnv(keys, defaultValue) {
  for (const key of keys) {
    // Check import.meta.env (Vite)
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      const val = parseInt(import.meta.env[key], 10);
      if (!isNaN(val) && val > 0) return val;
    }
    // Check process.env (Create React App / Node)
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      const val = parseInt(process.env[key], 10);
      if (!isNaN(val) && val > 0) return val;
    }
  }
  return defaultValue;
}

// Maximum number of log entries to retain (configurable via env)
const MAX_LOG_ENTRIES_DEFAULT = 5000;
export const MAX_LOG_ENTRIES = readNumEnv(
  ['VITE_MAX_AUDIT_LOGS', 'REACT_APP_MAX_AUDIT_LOGS'],
  MAX_LOG_ENTRIES_DEFAULT
);

// Counter for generating unique IDs within same millisecond
let idCounter = 0;

function prune(logs) {
  const cutoff = Date.now() - MAX_AGE;
  return logs.filter((l) => new Date(l.time).getTime() >= cutoff);
}

export function getLogs() {
  try {
    const stored = JSON.parse(localStorage.getItem(LOG_KEY)) || [];
    const pruned = prune(stored);
    if (pruned.length !== stored.length) {
      localStorage.setItem(LOG_KEY, JSON.stringify(pruned));
    }
    return pruned.sort((a, b) => new Date(b.time) - new Date(a.time));
  } catch (e) {
    return [];
  }
}

/**
 * Generates a unique ID for log entries
 * Combines timestamp with counter and random suffix to prevent collisions
 * @returns {string} Unique identifier
 */
function generateUniqueId() {
  const timestamp = Date.now();
  idCounter = (idCounter + 1) % 1000; // Reset counter after 1000
  const random = Math.random().toString(36).substring(2, 5); // 3-char random suffix
  return `${timestamp}-${idCounter}-${random}`;
}

export function addLog(entry) {
  const logs = getLogs();
  const obj = typeof entry === 'string' ? { message: entry } : entry;
  const logEntry = { id: generateUniqueId(), time: new Date().toISOString(), ...obj };
  logs.push(logEntry);
  
  // Enforce max log entries limit (trim oldest first)
  let trimmedLogs = logs;
  if (logs.length > MAX_LOG_ENTRIES) {
    // Sort by time descending, take the most recent MAX_LOG_ENTRIES
    trimmedLogs = logs
      .sort((a, b) => new Date(b.time) - new Date(a.time))
      .slice(0, MAX_LOG_ENTRIES);
  }
  
  localStorage.setItem(LOG_KEY, JSON.stringify(trimmedLogs));
  return logEntry;
}
